<template>
  <div
    v-if="!showEditTerminalButton"
    :class="$style.terminalWrapper"
  >
    <div :class="$style.terminalInfo">
      <img :src="`/img/salonized-pay/onboarding/${selectedTerminalModel}.png`">
      <Flex
        :direction="'column'"
        :gap="0.5"
      >
        <BaseHeading v-test="'sp-terminal-order-name'">
          {{ selectedTerminal?.name }}
        </BaseHeading>
        <div :class="$style.terminalDetails">
          <div
            v-for="feature in features"
            :key="feature"
          >
            <BaseText>
              {{ $t(
                `register.salonized_pay.landing.terminals.modal.info.${feature}`
              ).toUpperCase() }}
            </BaseText>
          </div>
        </div>
      </Flex>
    </div>
    <BaseButton
      v-if="!showEditTerminalButton"
      icon="edit"
      color="inverted"
      @click="editTerminal"
      v-test="'sp-terminal-order-edit'"
    />
  </div>
  <div
    v-else
    :class="$style.terminalPicker"
    v-test="'sp-terminal-order-picker'"
  >
    <div
      v-for="(terminal, idx) in terminals"
      :key="terminal.id"
      :class="$style.terminalOptionWrapper"
      @click="handlePickTerminal(terminal, idx)"
      v-test="'sp-terminal-order-picker-option'"
    >
      <BaseCard
        fullWidth
        :mb="idx === 0"
        :selected="pickedTerminalIdx === idx"
      >
        <div :class="$style.terminalOptionWrapper">
          <div :class="$style.terminalOption">
            <BaseRadio
              v-model="pickedTerminalIdx"
              :options="[{ value: idx, label: null }]"
            />
            <BaseText bold>{{ terminal.name }}</BaseText>
          </div>
          <BaseText>
            {{ filters.currency(terminal.price, terminal.currency) }}
          </BaseText>
        </div>
        <div
          v-if="pickedTerminalIdx === idx"
          :class="$style.infoContainer"
        >
          <img :src="`/img/salonized-pay/onboarding/${terminal.key}.png`">
          <div :class="$style.infoArray">
            <BaseText
              v-for="(info, idx) in terminal.info"
              :key="idx"
            >
              <li :class="$style.info">
                {{ $t(
                    `register.salonized_pay.landing.terminals.modal.info.${info}`
                  ) }}
              </li>
            </BaseText>
          </div>
        </div>
      </BaseCard>
    </div>
  </div>
  <div :class="$style.summary">
    <BaseHeading
      mt
      mb
    >
      {{ $t('register.salonized_pay.terminal_checkout.summary') }}
    </BaseHeading>
    <div :class="$style.summaryRow">
      <BaseText>{{ terminals[pickedTerminalIdx]?.name }}</BaseText>
      <BaseText v-test="'sp-terminal-order-price'">
        {{ filters.currency(price, selectedTerminal?.currency) }}
      </BaseText>
    </div>
    <div :class="$style.summaryRow">
      <BaseText v-test="'sp-terminal-order-vat-rate'">
        {{ `${$t('global.vat')} (${Math.round(vatRate * 100)}%)` }}
      </BaseText>
      <BaseText v-test="'sp-terminal-order-vat'">
        {{ filters.currency(vat, selectedTerminal?.currency) }}
      </BaseText>
    </div>
    <div :class="[$style.summaryRow, $style.totalRow]">
      <BaseText bold>{{ $t('global.total') }}</BaseText>
      <BaseText
        bold
        v-test="'sp-terminal-order-total'"
      >
        {{ filters.currency(total, selectedTerminal?.currency) }}
      </BaseText>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import filters from '@/filters';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { TerminalModel } from '@/types';
import { useTerminalData } from '../../../terminals';

const { terminals } = useTerminalData();

const { adyenAppState } = storeToRefs(useAdyenStore());
const { company } = useCompanyStore();

const selectedTerminalModel = computed(() => {
  const model = adyenAppState.value.terminalModel;
  if (model && (model === TerminalModel.Ams1 || model === TerminalModel.S1F2)) {
    return adyenAppState.value.terminalModel;
  }
  return 'AMS1';
});

const selectedTerminal = computed(() =>
  terminals.value.find(
    (terminal) => terminal.key === selectedTerminalModel.value,
  ),
);
const price = computed(() => terminals.value[pickedTerminalIdx?.value]?.price);
const vatRate = computed(() => {
  const formats = {
    nl: {
      vat: 0.21,
    },
    de: {
      vat: 0,
    },
    be: {
      vat: 0.21,
    },
  };
  return formats[company.country as keyof typeof formats]?.vat || 0;
});

const vat = computed(() => price.value * vatRate.value);

const total = computed(() => price.value + vat.value);
const features = computed(
  () => terminals.value[pickedTerminalIdx?.value]?.info,
);

const showEditTerminalButton = ref(!adyenAppState.value.terminalModel);
const pickedTerminalIdx = ref(0);

watch(
  () => selectedTerminal.value,
  () => {
    pickedTerminalIdx.value = terminals.value.findIndex(
      (terminal) => terminal.key === selectedTerminal.value?.key,
    );
  },
  { immediate: true },
);

const handlePickTerminal = (terminal: any, idx: any) => {
  pickedTerminalIdx.value = idx;
  adyenAppState.value.terminalModel = terminal.key;
};

const editTerminal = () => {
  showEditTerminalButton.value = true;
};
</script>

<style
  lang="scss"
  module
>
.terminalPicker {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: $spacing;
  margin: -$spacing;
  margin-bottom: $spacing;

  @include theme('light') {
    background: linear-gradient(
      115deg,
      #c0ede7 0%,
      #f4f5f9 45% 70%,
      #bab9e7 100%
    );
  }

  @include theme('dark') {
    background: linear-gradient(
      115deg,
      var(--color-success-darker) 0%,
      var(--color-bg) 45% 70%,
      var(--color-primary-darker) 100%
    );
  }
}

.terminalOptionWrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.terminalOption {
  display: flex;
  place-items: center;
}

.infoArray {
  display: flex;
  flex-direction: column;

  li::marker {
    color: var(--color-primary);
  }
}

.infoContainer {
  display: flex;
  gap: $spacing * 2;
  margin-top: $spacing;
}

.terminalOptionWrapper {
  width: 100%;
  cursor: pointer;
}

.terminalWrapper {
  display: flex;
  align-items: center;
  gap: $spacing;
  justify-content: space-between;
  padding: $spacing;
  border-radius: $radius;

  @include theme('light') {
    background: linear-gradient(
      115deg,
      #c0ede7 0%,
      #f4f5f9 45% 70%,
      #bab9e7 100%
    );
  }

  @include theme('dark') {
    background: linear-gradient(
      115deg,
      var(--color-success-darker) 0%,
      var(--color-bg) 45% 70%,
      var(--color-primary-darker) 100%
    );
    border: 1px solid var(--color-border);
  }
}

.terminalInfo {
  display: flex;
  align-items: center;
  gap: $spacing;
}

.terminalDetails {
  display: flex;
  gap: $spacing * .5;
  font-size: 10px;
  white-space: nowrap;
  font-size: 10px;
  flex-wrap: wrap;
}

.summaryRow {
  display: flex;
  justify-content: space-between;

  :first-child & {
    margin-bottom: $spacing * .5;
  }
}

.totalRow {
  margin-top: $spacing;
  padding-top: $spacing;
  border-top: 1px solid var(--color-border);
}
</style>
